import type React from "react";

import type { QuestionDto } from "@/api/types";
import { FormInput } from "@/components/Form/FormInput";
import { FormStarInput } from "@/components/Form/FormStarInput";
import { SectionCard } from "@/design-system/SectionCard";
import { useTranslation } from "@/translations";

import type { SurveyRespondFormValues } from "../SurveyRespondPage";
import {
  SurveyRespondFormBinaryChoiceInput,
  SurveyRespondFormMultipleChoiceInput,
} from "./SurveyRespondFormMultipleChoiceInput";
import { SurveyRespondFormRankingInput } from "./SurveyRespondFormRankingInput";

interface SurveyRespondQuestionCardProps {
  question: QuestionDto;
}

export function SurveyRespondQuestionCard({ question }: SurveyRespondQuestionCardProps): React.ReactNode {
  const { t } = useTranslation();

  let helperLabel = "";
  if (question.type === "multipleChoice" || question.type === "yesNo") {
    helperLabel = t("page.survey-respond.form.label.multiple-choice");
  }

  return (
    <SectionCard title={question.question} description={helperLabel}>
      {question.type === "open" && (
        <FormInput<SurveyRespondFormValues, string>
          data-testid="survey-respond-open-question-input"
          name={`${question.id}.text`}
          placeholder={t("page.survey-respond.form.placeholder.open-question")}
          rules={{
            required: t("components.form.error.required-generic"),
          }}
        />
      )}
      {question.type === "rating" && (
        <FormStarInput<SurveyRespondFormValues, string>
          data-testid="survey-respond-rating-input"
          name={`${question.id}.rating`}
        />
      )}
      {question.type === "yesNo" && <SurveyRespondFormBinaryChoiceInput questionId={question.id} />}
      {question.type === "multipleChoice" && (
        <SurveyRespondFormMultipleChoiceInput questionId={question.id} offeredAnswers={question.answers || []} />
      )}
      {question.type === "ranking" && (
        <SurveyRespondFormRankingInput questionId={question.id} offeredAnswers={question.answers || []} />
      )}
    </SectionCard>
  );
}
